import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Dumbbell, ClipboardList, CalendarPlus } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

const ITALIAN_MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];
const ITALIAN_WEEKDAYS_SHORT = ["L", "M", "M", "G", "V", "S", "D"];

function fmtKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function classifyEvent(e) {
  if (e.type === "training") return { kind: "training", label: e.title || "Allenamento", icon: Dumbbell, chip: "bg-violet-100 text-violet-700 border-violet-200" };
  if (e.type === "match") return { kind: "match", label: e.title || (e.opponent ? `vs ${e.opponent}` : "Partita"), icon: ClipboardList, chip: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  return { kind: "custom", label: e.title || "Evento", icon: CalendarPlus, chip: "bg-amber-100 text-amber-700 border-amber-200" };
}

/**
 * Drag-and-drop month-grid calendar.
 * Props:
 *  - events: [{ id, type, date, title, opponent, location, time }]
 *  - canEdit: bool (drag disabled if false)
 *  - onReload: () => void  (called after a successful move)
 *  - onSelectDate: (Date) => void
 *  - selectedDate: Date
 */
export default function DragDropMonthCalendar({ events, canEdit, onReload, onSelectDate, selectedDate }) {
  const [cursor, setCursor] = useState(() => {
    const d = selectedDate || new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [dragOverKey, setDragOverKey] = useState(null);
  const [moving, setMoving] = useState(false);

  const monthInfo = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // Italian week starts Monday: getDay() 0=Sun..6=Sat. Convert to 0=Mon..6=Sun.
    const startWeekday = (firstOfMonth.getDay() + 6) % 7;
    const cells = [];
    // Leading empty cells from previous month
    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      cells.push({ date: d, otherMonth: true });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push({ date: new Date(year, month, day), otherMonth: false });
    }
    // Trailing empty cells to complete the last week (6 rows × 7 cols = 42 max)
    while (cells.length % 7 !== 0) {
      const last = cells[cells.length - 1].date;
      const next = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
      cells.push({ date: next, otherMonth: true });
    }
    return { year, month, cells };
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (events || []).forEach((e) => {
      if (!e.date) return;
      (map[e.date] ||= []).push(e);
    });
    return map;
  }, [events]);

  const goPrev = () => setCursor(new Date(monthInfo.year, monthInfo.month - 1, 1));
  const goNext = () => setCursor(new Date(monthInfo.year, monthInfo.month + 1, 1));
  const goToday = () => {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    onSelectDate?.(t);
  };

  const onDragStart = (ev, item) => {
    if (!canEdit) {
      ev.preventDefault();
      return;
    }
    ev.dataTransfer.effectAllowed = "move";
    ev.dataTransfer.setData("text/plain", JSON.stringify({ id: item.id, type: item.type, fromDate: item.date }));
  };

  const onDragOver = (ev, key) => {
    if (!canEdit) return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };

  const onDragLeave = () => setDragOverKey(null);

  const onDrop = async (ev, targetDate) => {
    ev.preventDefault();
    setDragOverKey(null);
    if (!canEdit || moving) return;
    let payload;
    try {
      payload = JSON.parse(ev.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }
    const targetKey = fmtKey(targetDate);
    if (!payload?.id || !payload?.type || payload.fromDate === targetKey) return;
    setMoving(true);
    try {
      await api.patch("/calendar/move", {
        type: payload.type === "match" ? "match" : payload.type === "training" ? "training" : "custom",
        id: payload.id,
        new_date: targetKey,
      });
      toast.success(`Evento spostato a ${targetDate.toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`);
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Spostamento fallito");
    } finally {
      setMoving(false);
    }
  };

  const todayKey = fmtKey(new Date());
  const selectedKey = selectedDate ? fmtKey(selectedDate) : null;

  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid="dnd-month-calendar">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={goPrev}
            data-testid="cal-prev-month"
            aria-label="Mese precedente"
          >
            <ChevronLeft size={18} />
          </Button>
          <div
            className="font-heading font-semibold text-lg min-w-[160px] text-center capitalize"
            data-testid="cal-current-month"
          >
            {ITALIAN_MONTHS[monthInfo.month]} {monthInfo.year}
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={goNext}
            data-testid="cal-next-month"
            aria-label="Mese successivo"
          >
            <ChevronRight size={18} />
          </Button>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={goToday}
          data-testid="cal-today-btn"
          className="rounded-full"
        >
          Oggi
        </Button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
        {ITALIAN_WEEKDAYS_SHORT.map((w, i) => (
          <div key={i} className="py-2 text-center">
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {monthInfo.cells.map(({ date, otherMonth }, idx) => {
          const key = fmtKey(date);
          const dayEvents = eventsByDate[key] || [];
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;
          const isDragOver = dragOverKey === key;
          const showCount = dayEvents.length > 3;
          const visible = dayEvents.slice(0, 3);

          return (
            <div
              key={`${key}-${idx}`}
              data-testid={`cal-day-${key}`}
              onDragOver={(ev) => onDragOver(ev, key)}
              onDragLeave={onDragLeave}
              onDrop={(ev) => onDrop(ev, date)}
              onClick={() => onSelectDate?.(date)}
              className={`min-h-[88px] sm:min-h-[110px] p-1.5 border-r border-b border-slate-100 cursor-pointer transition-colors ${
                otherMonth ? "bg-slate-50/50 text-slate-300" : "bg-white text-slate-700"
              } ${isDragOver ? "bg-sky-50 ring-2 ring-inset ring-sky-400" : ""} ${
                isSelected && !isDragOver ? "bg-sky-50/60" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${
                    isToday ? "bg-sky-500 text-white" : ""
                  }`}
                >
                  {date.getDate()}
                </span>
              </div>

              <div className="space-y-1">
                {visible.map((e) => {
                  const meta = classifyEvent(e);
                  const Icon = meta.icon;
                  return (
                    <div
                      key={`${e.type}-${e.id}`}
                      draggable={canEdit}
                      onDragStart={(ev) => onDragStart(ev, e)}
                      data-testid={`cal-event-chip-${e.id}`}
                      title={`${meta.label}${e.time ? " · " + e.time : ""}${e.meeting_time ? " · ritrovo " + e.meeting_time : ""}`}
                      className={`text-[10px] sm:text-[11px] truncate rounded px-1.5 py-0.5 border ${meta.chip} ${
                        canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default"
                      }`}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <Icon size={10} className="inline mr-1 -mt-0.5" />
                      {meta.label}
                    </div>
                  );
                })}
                {showCount && (
                  <div className="text-[10px] text-slate-500 px-1.5">
                    +{dayEvents.length - 3} altri
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="px-4 py-2 border-t border-slate-100 text-[11px] text-slate-500 bg-slate-50">
          Trascina un evento su un altro giorno per spostarlo.
        </div>
      )}
    </div>
  );
}
